/**
 * Disables option values that cannot be combined with the values chosen in the other option groups.
 *
 * The server renders the incompatible value pairs, derived from the stock product sets, in the
 * data-option-incompatibilities attribute of the form: { "<value id>": [<value id>, ...] }.
 * Radio cards and plain selects get their conflicting choices disabled. The colour picker and the
 * Typesense colour select load their colours remotely: they ask getUnavailableValueIds() which
 * colours to grey out, whenever they render and after each OPTION_COMBINATIONS_UPDATED_EVENT.
 * Choosing a colour clears the choices in other groups it conflicts with. The cart validates the
 * combination again, so this is only a convenience.
 */

const FORM_SELECTOR = '[data-product-configurator]'
const OPTION_SELECTOR = '[data-product-configurator-option]'
const HINT_SELECTOR = '[data-option-combination-hint]'

// Dispatched on the form after the choices were updated, so the remote colour lists can grey out their colours again
export const OPTION_COMBINATIONS_UPDATED_EVENT = 'option-combinations:updated'

export function applyOptionCombinations(form, changedField = null) {
  const incompatibilities = parseIncompatibilities(form.dataset.optionIncompatibilities)

  if (incompatibilities.size === 0) {
    return
  }

  const groups = getGroups(form)

  if (changedField instanceof Element) {
    clearConflicts(groups, getGroupName(changedField), incompatibilities)
  } else {
    // On load, earlier groups (colour comes first) win over later ones
    groups.forEach((group, name) => clearConflicts(groups, name, incompatibilities))
  }

  groups.forEach((group, name) => updateDisabledChoices(form, groups, name, incompatibilities))

  form.dispatchEvent(new CustomEvent(OPTION_COMBINATIONS_UPDATED_EVENT))
}

/**
 * The value ids that cannot be chosen in the group of the given option field, because they conflict
 * with what is selected in the other groups. Empty for products without incompatibilities.
 */
export function getUnavailableValueIds(field) {
  const form = field instanceof Element ? field.closest(FORM_SELECTOR) : null

  if (!(form instanceof HTMLFormElement)) {
    return new Set()
  }

  const incompatibilities = parseIncompatibilities(form.dataset.optionIncompatibilities)
  const unavailable = new Set()

  getSelectedElsewhere(getGroups(form), getGroupName(field)).forEach((selected) => {
    incompatibilities.get(String(selected))?.forEach((id) => unavailable.add(id))
  })

  return unavailable
}

/**
 * The visible "not available in this combination" label of a greyed out colour. The colour buttons are
 * too narrow on mobile for the full text, so a shorter one replaces it below md, the breakpoint at which
 * the configure and colour picker modals go full screen. Both texts come from the form's data attributes.
 */
export function createUnavailableLabel(form, className = '') {
  const text = form?.dataset.optionUnavailableText ?? ''
  const shortText = form?.dataset.optionUnavailableShortText || text
  const label = document.createElement('span')
  const long = document.createElement('span')
  const short = document.createElement('span')

  label.className = className
  long.className = 'd-none d-md-inline'
  long.textContent = text
  short.className = 'd-md-none'
  short.textContent = shortText
  label.append(long, short)

  return label
}

function parseIncompatibilities(value) {
  const map = new Map()

  try {
    const data = JSON.parse(value ?? '{}')

    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([id, ids]) => {
        if (Array.isArray(ids)) {
          map.set(String(id), new Set(ids.map(String)))
        }
      })
    }
  } catch {
    // An invalid attribute disables nothing; the cart still validates the combination
  }

  return map
}

const isRadio = (field) => field instanceof HTMLInputElement && field.type === 'radio'

const isManagedSelect = (field) => field instanceof HTMLSelectElement && !field.hasAttribute('data-typesense-product-color-select')

const getGroupName = (field) => field.name

function getGroups(form) {
  const groups = new Map()

  form.querySelectorAll(OPTION_SELECTOR).forEach((field) => {
    const name = getGroupName(field)

    if (!name) {
      return
    }

    if (!groups.has(name)) {
      groups.set(name, [])
    }

    groups.get(name).push(field)
  })

  return groups
}

function getSelectedValue(fields) {
  for (const field of fields) {
    if (isRadio(field)) {
      if (field.checked) {
        return field.value
      }

      continue
    }

    return field.value || null
  }

  return null
}

const conflicts = (incompatibilities, a, b) => Boolean(a && b && incompatibilities.get(String(a))?.has(String(b)))

function clearConflicts(groups, changedName, incompatibilities) {
  const value = getSelectedValue(groups.get(changedName) ?? [])

  if (!value) {
    return
  }

  groups.forEach((fields, name) => {
    if (name === changedName || !conflicts(incompatibilities, value, getSelectedValue(fields))) {
      return
    }

    fields.forEach((field) => {
      if (isRadio(field)) {
        field.checked = false
      } else if (isManagedSelect(field)) {
        if (field.tomselect) {
          field.tomselect.clear(true)
        } else {
          field.value = ''
        }
      }
    })
  })
}

function getSelectedElsewhere(groups, name) {
  const selectedElsewhere = []

  groups.forEach((fields, otherName) => {
    const value = otherName === name ? null : getSelectedValue(fields)

    if (value) {
      selectedElsewhere.push(value)
    }
  })

  return selectedElsewhere
}

function updateDisabledChoices(form, groups, name, incompatibilities) {
  const selectedElsewhere = getSelectedElsewhere(groups, name)
  const isUnavailable = (value) => selectedElsewhere.some((selected) => conflicts(incompatibilities, value, selected))

  groups.get(name).forEach((field) => {
    if (isRadio(field)) {
      setDisabled(field, isUnavailable(field.value))
      updateHint(form, field)
    } else if (isManagedSelect(field)) {
      Array.from(field.options).forEach((option) => {
        if (option.value) {
          setDisabled(option, isUnavailable(option.value))
          updateTomSelectOption(field, option)
        }
      })
    }
  })
}

/*
 * Choices that the server already disabled (e.g. sold out) stay disabled. That state is
 * remembered on first use, as the disabled attribute is overwritten afterwards.
 */
function setDisabled(element, unavailable) {
  if (element.dataset.optionCombinationDisabled === undefined) {
    element.dataset.optionCombinationDisabled = element.disabled ? 'server' : ''
  }

  element.disabled = element.dataset.optionCombinationDisabled === 'server' || unavailable
  element.toggleAttribute('data-option-combination-unavailable', unavailable)
}

function updateTomSelectOption(select, option) {
  const tomSelect = select.tomselect
  const data = tomSelect?.options?.[option.value]

  if (!data || Boolean(data.disabled) === option.disabled) {
    return
  }

  tomSelect.updateOption(option.value, { ...data, disabled: option.disabled })
}

/*
 * The radio card layouts differ, so the reason is announced to screen readers and shown
 * as a tooltip instead of changing the card.
 */
function updateHint(form, input) {
  const label = input.id ? form.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null

  if (!(label instanceof HTMLElement)) {
    return
  }

  const unavailable = input.hasAttribute('data-option-combination-unavailable')
  const text = form.dataset.optionUnavailableText || ''
  let hint = label.querySelector(HINT_SELECTOR)

  if (!unavailable) {
    hint?.remove()

    if (label.title === text) {
      label.removeAttribute('title')
    }

    return
  }

  label.title = text

  if (hint) {
    return
  }

  hint = document.createElement('span')
  hint.className = 'visually-hidden'
  hint.toggleAttribute('data-option-combination-hint', true)
  hint.textContent = ` (${text})`
  label.append(hint)
}
